export interface StudySection {
  readonly id: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly summary: string;
  readonly concepts: readonly string[];
  readonly checkpoint: string;
}

export const STUDY_SECTIONS = [
  {
    id: "internet-request",
    eyebrow: "Foundation 01",
    title: "The life of an Internet request",
    summary: "A user enters a hostname. A recursive resolver asks DNS for an address. With a proxied Cloudflare record, DNS returns Cloudflare anycast addresses. The client establishes transport and TLS with a nearby Cloudflare edge. Cloudflare evaluates security and delivery controls, then creates a separate connection to the origin for allowed traffic.",
    concepts: [
      "DNS maps names to routing information; it does not carry the web page itself.",
      "A reverse proxy accepts the visitor connection and makes a separate origin connection.",
      "HTTP method, host, path, headers, query, cookies, and body describe an application request.",
      "A response carries a status code, headers, and often a body.",
      "Anycast allows the same network prefix to be advertised from many locations.",
    ],
    checkpoint: "Draw visitor → resolver → Cloudflare edge → origin and explain what each component knows and controls.",
  },
  {
    id: "dns",
    eyebrow: "Foundation 02",
    title: "DNS, zones, records, and proxy status",
    summary: "Cloudflare can be the authoritative DNS provider for a zone. A proxied A, AAAA, or CNAME record returns Cloudflare proxy addresses and places supported HTTP traffic behind Cloudflare. A DNS-only record exposes the configured destination and does not receive reverse-proxy protection.",
    concepts: [
      "A registrar records domain ownership; authoritative nameservers publish the zone's records.",
      "A, AAAA, and CNAME records answer different naming needs.",
      "TTL influences resolver caching but is not a security policy.",
      "DNSSEC authenticates DNS data and detects tampering; it does not encrypt DNS queries.",
      "Origin exposure can let attackers bypass edge controls even when the main hostname is proxied.",
    ],
    checkpoint: "Given a hostname, determine whether traffic is proxied and describe the consequences of changing it to DNS-only.",
  },
  {
    id: "tls",
    eyebrow: "Foundation 03",
    title: "TLS, certificates, and the two-connection model",
    summary: "For proxied HTTPS, the visitor negotiates TLS with Cloudflare and Cloudflare separately connects to the origin. Encryption mode governs the origin leg. Full (strict) validates the origin certificate; Full encrypts but does not validate it. Cloudflare Origin CA certificates are intended for Cloudflare-to-origin trust, not direct browser trust.",
    concepts: [
      "A certificate binds names to a public key through an issuer's signature.",
      "The private key must remain private; the certificate can be public.",
      "Full (strict) is the standard target when the origin has a valid public or Origin CA certificate.",
      "Authenticated Origin Pulls use client authentication so the origin can verify Cloudflare.",
      "mTLS authenticates both sides and is useful for API clients and service connections.",
    ],
    checkpoint: "Explain why an Origin CA certificate can work in Full (strict) but show a browser trust error after proxying is disabled.",
  },
  {
    id: "app-security",
    eyebrow: "Application Security 01",
    title: "Application security control plane",
    summary: "Cloudflare inspects proxied HTTP and API requests at the edge. Security Analytics shows incoming traffic and detections. Security Events explains mitigations. Security Center surfaces broader findings, while client-side security observes browser scripts and connections. WAF, rate limiting, API Shield, and bot controls solve different problems and must be reasoned about in execution order.",
    concepts: [
      "Account security, least privilege, scoped API tokens, 2FA, and audit logs protect the control plane.",
      "Always-on DDoS protection addresses volumetric and protocol attacks at appropriate layers.",
      "Managed rules provide maintained detections; custom rules express application-specific policy.",
      "Lists centralize reusable values instead of duplicating them across rules.",
      "Every exception should be narrower than the protection it bypasses.",
      "Review and revoke suspicious active sessions; MFA does not replace incident response.",
    ],
    checkpoint: "Choose the correct product for SQL injection, credential stuffing, API schema abuse, automated scraping, and a Layer 3 routed-network attack.",
  },
  {
    id: "rules",
    eyebrow: "Application Security 02",
    title: "Rules, phases, actions, and false positives",
    summary: "The simplified application-security flow is HTTP DDoS, custom rules, rate limiting, Managed Rules, then Super Bot Fight Mode. Account-level rulesets run before zone-level rulesets within a phase. Bot Fight Mode is outside the Ruleset Engine. Terminating actions stop later work, and Skip affects only supported targets.",
    concepts: [
      "Block and Managed Challenge can terminate evaluation.",
      "Log is observational where available; Skip changes later rule evaluation.",
      "A broad exception is an authorization change, not a harmless troubleshooting toggle.",
      "Use event details to capture service, ruleset, rule ID, action, and common request properties.",
      "Correct the smallest proven false-positive condition and retest expected malicious and valid traffic.",
      "A Ruleset Engine Skip cannot exempt traffic from Bot Fight Mode.",
    ],
    checkpoint: "Explain why a Managed Rules event is absent when an earlier custom Block action matched the request.",
  },
  {
    id: "api-bots",
    eyebrow: "Application Security 03",
    title: "API Shield and bot controls",
    summary: "API Shield builds an inventory of Web Assets and Operations and applies positive and behavioral controls. Schema validation checks an expected contract. JWT validation verifies signatures and supported temporal claims; custom rules enforce application-specific verified claims. mTLS identifies certificate-bearing clients. Sequence, abuse, and bot controls examine behavior.",
    concepts: [
      "A session identifier should distinguish API clients consistently enough for per-session analytics and controls.",
      "Start new schema enforcement in log or observation mode and evaluate legitimate violations before blocking.",
      "Low bot scores indicate greater confidence in automation; high scores indicate greater confidence in a human.",
      "Verified bots identify transparent, non-abusive automated actors; verification is not equivalent to all-purpose authorization.",
      "Rate limits should use the best available actor identity rather than grouping unrelated users behind one shared IP.",
    ],
    checkpoint: "Design protection for an authenticated funds-transfer API: identity, schema, rate, and sequence controls.",
  },
  {
    id: "zero-trust",
    eyebrow: "Zero Trust 01",
    title: "Zero Trust, ZTNA, SSE, and SASE",
    summary: "Zero Trust rejects the assumption that network location creates trust. Access decisions use identity, device, resource, and contextual signals. ZTNA provides least-privilege access to private resources. SSE groups cloud-delivered security services. SASE combines networking and security architecture for distributed users, devices, applications, and networks.",
    concepts: [
      "Cloudflare Access is the identity-aware application and private-resource policy layer.",
      "Cloudflare Gateway is the DNS, network, and HTTP traffic policy layer.",
      "Cloudflare One Client is a device on-ramp and posture signal source.",
      "Cloudflare Tunnel and Cloudflare Mesh connect private resources and networks through different traffic models.",
      "Least privilege means granting the smallest resource scope, not merely authenticating once.",
    ],
    checkpoint: "Explain Access versus Gateway and give one scenario where both protect the same user's workflow.",
  },
  {
    id: "identity-device",
    eyebrow: "Zero Trust 02",
    title: "Identity, enrollment, profiles, and posture",
    summary: "The identity provider authenticates users and supplies attributes. Enrollment policies decide who may register devices. Device profiles decide client configuration. The enrolled client can then report supported posture signals. Access and Gateway policies consume those signals at enforcement time.",
    concepts: [
      "Confirm that required IdP group attributes actually appear in Cloudflare's user registry.",
      "A one-time PIN verifies control of an address but does not automatically prove company membership.",
      "Posture checks occur after enrollment; use enrollment-specific controls to admit the device first.",
      "Split Tunnel Exclude sends everything except listed destinations through Cloudflare; Include sends only listed destinations.",
      "Device profiles are first-match with a Default profile; supported MDM values override conflicting dashboard settings.",
      "Prefer resolver policies when Cloudflare can reach the private resolver; Local Domain Fallback is client-side, acts first on overlap, and bypasses Gateway DNS policy and logs.",
    ],
    checkpoint: "Design enrollment and application policies for employees, contractors, managed laptops, and an emergency exception without using Include Everyone.",
  },
  {
    id: "connectivity",
    eyebrow: "Zero Trust 03",
    title: "Tunnel, Mesh, routing, and private DNS",
    summary: "cloudflared creates outbound, proxied connections and is the straightforward choice for publishing applications or user-to-network access without inbound ports. Cloudflare Mesh runs the Cloudflare One Client headlessly on Linux as a Layer 3 participant, supporting bidirectional, site-to-site, server-initiated, and long-lived flows.",
    concepts: [
      "Tunnel proxies connections; origin applications see the connector-side source context unless application headers provide other context.",
      "Mesh preserves a Layer 3 model and supports TCP, UDP, and ICMP bidirectionally.",
      "Private routes describe reachable CIDRs; overlapping ranges require explicit design.",
      "Split Tunnel steering and connector routes must align; virtual networks can separate overlapping private address spaces.",
      "Production connectors need multiple instances and deliberate placement.",
      "If private IP access works but the hostname fails, investigate DNS routing before network reachability.",
    ],
    checkpoint: "Select Tunnel or Mesh for an internal website, site-to-site VoIP, persistent database replication, and server-initiated device management.",
  },
  {
    id: "access-gateway",
    eyebrow: "Zero Trust 04",
    title: "Access and Gateway policy logic",
    summary: "Access requires Include. Includes are OR, Requires are AND, Excludes are NOT, and an OR-based rule group supplies alternatives inside one required condition. Service Auth and Bypass form the early action class; Block and Allow follow. Gateway DNS, network, and HTTP builders have distinct enforcement models.",
    concepts: [
      "Access: Service Auth and Bypass run in displayed relative order; Block and Allow then run in displayed order until a match.",
      "Gateway network policies use first match; specific exceptions belong above general rules.",
      "Gateway HTTP evaluates Do Not Inspect, then Isolate, then ordinary Allow/Block/Do Not Scan, then body scanning.",
      "A matching Do Not Inspect policy bypasses remaining HTTP policies, so scope it tightly.",
      "Gateway is permissive for unmatched traffic unless a restrictive policy design is configured.",
    ],
    checkpoint: "Given five overlapping policies, calculate the evaluation order and final action without guessing from the policy names.",
  },
  {
    id: "data-operations",
    eyebrow: "Zero Trust 05",
    title: "TLS inspection, DLP, CASB, isolation, and operations",
    summary: "TLS inspection enables application-aware policy by decrypting and re-encrypting traffic with a trusted root. DLP profiles define sensitive data; Gateway applies them in transit and supported CASB integrations can apply them at rest. Browser Isolation executes web content remotely. CASB scans SaaS periodically and out of band. Logs and client diagnostics close the loop.",
    concepts: [
      "Certificate-pinned applications may need precise Do Not Inspect policy, not a broad Allow.",
      "DLP and antivirus body scanning occur after earlier HTTP policy stages.",
      "Browser Isolation changes where active code executes; it is not merely a URL category block.",
      "CASB posture findings complement inline Gateway policy.",
      "Browser Isolation is different from Access browser rendering for SSH, RDP, and VNC.",
      "Troubleshoot in boundaries: device → identity → route/DNS → policy → connector → resource.",
    ],
    checkpoint: "Diagnose a TLS error, a private DNS failure, an unexpected block, a stale device profile, and a failed connector using the correct evidence for each boundary.",
  },
] as const satisfies readonly StudySection[];
